import { JobApiService } from 'jobs/JobApiService';
import {
   CreateJobDto,
   CreateJobEmpty,
   CreateJobEmptyResponse,
   CreateJobResponse,
   JobLocality,
   JobPlan,
} from 'jobs/types';
import { autorun, computed, makeObservable, observable, runInAction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { JobLangGroupModel } from 'jobs/job-form/job-lang-list';
import { SkillsViewModel } from 'jobs/models/skills.view-model';
import { JobLocationViewModel } from 'jobs/job-form/job-location';
import { SalaryGroupViewModel } from './job-salary-group';
import { JobCategoriesGroupModel } from './job-categories-group';
import { FormControl, FormGroup } from 'shared/libs/form';
import { Validators } from 'core/services/Validators';
import { AsyncState } from 'shared/libs/state';

import { ApiError } from 'shared/types';
import { DateUtilsService } from 'shared/utils/date-utils.service';

import { JobViewStore } from './job-view-dialog';
type Mode = 'CREATE' | 'EDIT' | 'COPY';
export class JobFormManager {
   readonly jobAttributes = observable.box<CreateJobEmpty | null>(null);

   readonly locationVm = new JobLocationViewModel(this);
   readonly salaryGroupVm = new SalaryGroupViewModel(this);
   readonly jobAttributtesState = new AsyncState<CreateJobEmptyResponse>();
   readonly createJobState = new AsyncState<CreateJobResponse>();
   MODE: Mode;
   readonly form = new FormGroup(
      {
         planId: new FormControl<string>('', [Validators.required]),
         job_title: new FormControl<string>('', [Validators.required]),
         category: new FormControl<string>('', [Validators.required]),
         category_1: new FormControl<string>(''),
         category_2: new FormControl<string>(''),
         subCategory: new FormControl<number[]>([], [Validators.required], { unwrap: true }),
         subCategory_1: new FormControl<number[]>([], [], { unwrap: true }),
         subCategory_2: new FormControl<number[]>([], [], { unwrap: true }),
         work_type: new FormControl<string>('', [Validators.required]),
         experience: new FormControl<string>('', [Validators.required]),
         form_link: new FormControl<string>('', [Validators.urlFormat]),
         skill: new FormControl<number[]>([], [], { unwrap: true }),
         lng_1: new FormControl<string>(''),
         lng_2: new FormControl<string>(''),
         lng_skill_1: new FormControl<string>(''),
         lng_skill_2: new FormControl<string>(''),
         salary_max: new FormControl<string>('', [Validators.salaryNumeric]),
         salary_min: new FormControl<string>('', [Validators.salaryNumeric]),
         salary_currency: new FormControl<string>(''),
         only_resume: new FormControl<boolean>(true),
         questionary: new FormControl<boolean>(true),
         /**
          * @countryId 5 - Ukraine id default
          */
         country: new FormControl<string>('5', [Validators.required]),
         city: new FormControl<string>('', [Validators.required]),
         jobDescription: new FormControl<string>('', [Validators.required]),
      },
      [
         (controls) => {
            if (
               !controls['salary_currency'].value &&
               (controls['salary_min'].value || controls['salary_max'].value)
            ) {
               return { salary_currency: "Валюта обов'язкова" };
            }

            return {};
         },
      ]
   );

   readonly categoriesGroup: JobCategoriesGroupModel;
   readonly langGroup: JobLangGroupModel;
   readonly skillsVm: SkillsViewModel;
   readonly jobView: JobViewStore;
   count = 0;
   constructor(mode: Mode) {
      makeObservable(this, {
         experinceOptions: computed,
         worktypeOptions: computed,
      });

      this.MODE = mode;

      this.categoriesGroup = new JobCategoriesGroupModel(this);
      this.langGroup = new JobLangGroupModel(this);

      this.skillsVm = new SkillsViewModel([], {
         onValueChange: (val) => {
            this.form.controls['skill'].setValue(val.map((skill) => skill.id));
         },
      });

      this.jobView = new JobViewStore(this);

      autorun(() => {
         console.log({ FORM_VALUE: this.form.value });
      });
   }

   get experinceOptions(): Array<{ id: string; label: string; value: string }> {
      const attr = this.jobAttributes.get();
      if (attr) {
         return Object.entries(attr.experience).map<{
            id: string;
            label: string;
            value: string;
         }>(([k, v]) => ({
            id: uuidv4(),
            value: k.toString(),
            label: v,
         }));
      }
      return [];
   }

   get worktypeOptions(): Array<{ id: string; label: string; value: string }> {
      const attr = this.jobAttributes.get();
      if (attr) {
         return Object.entries(attr.worktype).map<{
            id: string;
            label: string;
            value: string;
         }>(([k, v]) => ({
            id: uuidv4(),
            value: k,
            label: v,
         }));
      }
      return [];
   }

   formatPlanViewDate(plan: JobPlan): string {
      const createdDate = DateUtilsService.toAppFormat(plan.created);
      const endedDate = DateUtilsService.toAppFormat(plan.end_date);
      return `[${createdDate} - ${endedDate}]`;
   }

   async fetchAttributtes(opts: {
      onSuccess?: () => void;
      onPlanError?: (e: string) => void;
   }): Promise<void> {
      await this.jobAttributtesState.run(() => JobApiService.jobCreateJob(null), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               runInAction(() => {
                  this.jobAttributes.set(resp.response);
                  this.skillsVm.setOptions(resp.response.skillList);
                  if (resp.response.planList.length === 1) {
                     const plan = resp.response.planList[0];
                     this.form.controls['planId'].setValue(plan.id.toString());
                  }
               });
               opts.onSuccess?.();
            }
            if (resp.status === 500) {
               opts?.onPlanError?.(resp.message);
            }
         },
      });
   }

   async createJob(opts: {
      onSuccess?: (res: CreateJobResponse) => void;
      onError?: (e: ApiError) => void;
   }): Promise<void> {
      const jobAttributes = this.jobAttributes.get();
      this.form.validate();

      if (!this.form.valid || !jobAttributes) {
         return;
      }
      const dto: CreateJobDto = {
         user_plan_id: Number(this.form.controls['planId'].value),
         job_title: this.form.controls['job_title'].value,
         category: this.form.controls['category'].value,
         category_2: this.form.controls['category_1'].value,
         category_3: this.form.controls['category_2'].value,
         subCategory: this.form.controls['subCategory'].value,
         subCategory_2: this.form.controls['subCategory_1'].value,
         subCategory_3: this.form.controls['subCategory_2'].value,
         experience: this.form.controls['experience'].value,
         lng_1: this.form.controls['lng_1'].value,
         lng_2: this.form.controls['lng_2'].value,
         lng_skill_1: this.form.controls['lng_skill_1'].value,
         lng_skill_2: this.form.controls['lng_skill_2'].value,
         work_type: this.form.controls['work_type'].value,
         skill: this.form.controls['skill'].value,

         salary_max: this.form.controls['salary_max'].value,
         salary_min: this.form.controls['salary_min'].value,
         salary_currency: this.form.controls['salary_currency'].value,

         jobDescription: this.form.controls['jobDescription'].value,

         country: Number(this.form.controls['country'].value),
         city: Number(this.form.controls['city'].value),
         form_link: this.form.controls['form_link'].value,

         only_resume: this.form.controls['only_resume'].value,
         questionary: this.form.controls['questionary'].value,

         company_name: jobAttributes.jobData.company_name,
         logo: jobAttributes.jobData.company_logo,
         companyProfile: jobAttributes.jobData.companyProfile,
         company_website: jobAttributes.jobData.company_website,
      };

      await this.createJobState.run(() => JobApiService.jobCreateJob(dto), {
         onSuccess: opts.onSuccess,
         onError: opts.onError,
      });
   }

   async loadJob(
      dto: { slugId: string },
      opts: {
         onSuccess?: () => void;
         onPlanError?: (e: string) => void;
      }
   ): Promise<void> {
      await this.jobAttributtesState.run(() => JobApiService.loadJob(dto), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               runInAction(() => {
                  this.jobAttributes.set(resp.response);
                  this.skillsVm.setOptions(resp.response.skillList);
                  this._fillFormFields(resp.response);
               });
               opts.onSuccess?.();
            }
            if (resp.status === 500) {
               opts?.onPlanError?.(resp.message);
            }
         },
      });
   }

   _fillFormFields(resp: CreateJobEmpty): void {
      const job = resp.job;
      if (!job) {
         return;
      }

      this.form.patchValue({
         planId: job.user_plan_id?.toString() ?? '',
         job_title: job.job_title ?? '',
         category: job.category?.toString() ?? '',
         category_1: job.category_2?.toString() ?? '',
         category_2: job.category_3?.toString() ?? '',
         subCategory: job.subcategory_id?.map((id) => Number(id)) ?? [],
         subCategory_1: job.subcategory_id_2.map((id) => Number(id)) ?? [],
         subCategory_2: job.subcategory_id_3.map((id) => Number(id)) ?? [],
         experience: job.experience?.toString() ?? '',
         work_type: job.work_type?.toString() ?? '',
         jobDescription: job.jobDescription ?? '',
         salary_min: job.salary_min ? job.salary_min.toString() : '',
         salary_max: job.salary_max ? job.salary_max.toString() : '',
         salary_currency: job.salary_currency?.toString() ?? '',
         skill: job.skill ?? [],
         lng_1: job.lng_1?.toString() ?? '',
         lng_2: job.lng_2?.toString() ?? '',
         lng_skill_1: job.lng_skill_1?.toString() ?? '',
         lng_skill_2: job.lng_skill_2?.toString() ?? '',
         city: job.city.toString() ?? '',
         form_link: job.form_link ?? '',
         only_resume: job.only_resume === 1,
         questionary: job.questionary === 1,
      });

      if (job.skill.length > 0) {
         const skills = resp.skillList.filter((skill) => {
            return job.skill.find((s) => Number(s) === skill.id);
         });
         this.skillsVm.multiselect.values.replace(skills);
      }
      const city: JobLocality = {
         id: job.city,
         name: job.city_region,
      };

      if (job.lng_2 && job.lng_skill_2) {
         this.langGroup.lngGroupItem2.show();
      }

      this.locationVm.cityVM.onSelect(city);
      this.locationVm.cities.replace([city]);
      this.categoriesGroup.restoreValues();
   }
}
